/**
 * THE BUSINESS'S OWN REMINDER DEFAULT: on, 30 minutes before every contest, by
 * email. Every new player starts with it and may change it themselves. One tap
 * switches it off and one tap puts it back.
 */
import { useCallback, useEffect, useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '../../../lib/api';

export const GoalReminderDefaultsBar = ({ organizationId, compact = false }) => {
    const { t } = useTranslation();
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);
    const r = (k, o) => t(`goalGame.reminderDefault.${k}`, o);

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const res = await api.get('/goal-game/admin/reminder-defaults',
                { params: { organization_id: organizationId } });
            setCfg(res.data);
        } catch (_e) { setCfg(null); }
    }, [organizationId]);

    useEffect(() => { load(); }, [load]);

    const save = async (patch) => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await api.put('/goal-game/admin/reminder-defaults', {
                organization_id: organizationId, ...patch });
            setCfg((prev) => ({ ...(prev || {}), ...res.data }));
            toast.success(res.data.on ? r('turnedOn') : r('turnedOff'));
        } catch (_e) { toast.error(r('failed')); } finally { setBusy(false); }
    };

    if (!cfg) return null;

    return (
        <div data-testid={`gg-reminder-default-${organizationId}`}
             className={compact
                 ? 'flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-slate-100 dark:border-slate-700'
                 : 'flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3'}>
            <span className={compact ? 'text-sm text-muted-foreground inline-flex items-center gap-1.5'
                : 'text-sm font-semibold inline-flex items-center gap-1.5'}>
                <Bell className="w-3.5 h-3.5" />{r('title')}
            </span>
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => save({ on: !cfg.on })} disabled={busy}
                        aria-pressed={!!cfg.on}
                        data-testid={`gg-reminder-default-switch-${organizationId}`}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold ${
                            cfg.on ? 'bg-emerald-600 text-white border-emerald-600'
                                : 'bg-white dark:bg-slate-900 text-muted-foreground border-slate-300 dark:border-slate-700'}`}>
                    {busy ? <Loader2 size={13} className="animate-spin" /> : null}
                    {cfg.on ? r('on') : r('off')}
                </button>
                {(cfg.choices || [15, 30, 60, 120]).map((m) => (
                    <button key={m} type="button" onClick={() => save({ minutes: m })}
                            disabled={busy || !cfg.on}
                            data-testid={`gg-reminder-default-${m}-${organizationId}`}
                            className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold disabled:opacity-50 ${
                                Number(cfg.minutes) === m
                                    ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900'
                                    : 'border-slate-300 dark:border-slate-700 text-muted-foreground'}`}>
                        {r('minutes', { minutes: m })}
                    </button>
                ))}
                <span className="text-xs text-muted-foreground">{r('byEmail')}</span>
            </div>
        </div>
    );
};

export default GoalReminderDefaultsBar;
